import { FC, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';

import { QuestionSessionBodyContainerProps } from '../../QuestionSessions/QuestionSessionBody/config';
import { useDiaryFrame } from '../useDiaryFrame';
import { useReferentialQuestionContent } from '../../../hooks/content/useReferentialQuestionContent';
import { useStores } from '../../../hooks/useStores';

export interface JournalEntryData extends Record<string, unknown> {
  value: string;
  onChangeText: (value: string) => void;
  infoTitle?: string;
  infoBody?: string;
}

const isToday = (date?: Date | null) =>
  !!date && new Date(date).toDateString() === new Date().toDateString();

/** the entry recorded today for the activity, if any */
const todayEntryOf = (activity: any): string | null =>
  activity?.days?.find((day: any) => day.done && day.entry && isToday(day.dateOfCompletion))?.entry || null;

/**
 * A day entry is composed of "question\nanswer" blocks (composeActivityEntry):
 * gives back the answer written under this question, or '' when absent.
 */
const answerFromEntry = (entry: string, questionText?: string | null): string => {
  const wording = (questionText || '').trim();
  if (!wording) {
    return '';
  }
  const block = entry.split('\n\n').find(candidate => candidate.split('\n')[0].trim() === wording);
  return block ? block.split('\n').slice(1).join('\n').trim() : '';
};

/**
 * Owns the journal text (pending response) and splits the optional helper
 * card out of the question description ("title|body").
 */
const _ContainJournalEntry: FC<QuestionSessionBodyContainerProps> = ({
  question,
  children,
}) => {
  const { treeNav } = useStores();
  const { question: questionStore } = useStores();
  const loading = !questionStore.isReady(question._id);
  const { description, question: questionText } = useReferentialQuestionContent(question);

  const value = treeNav.choices.pending?.freeText || '';

  // re-opening today's diary (overview "Update"): start from what was written
  // rather than a blank field. Once per question, so a field the user then
  // empties on purpose stays empty.
  const { activity } = useDiaryFrame();
  const { activity: activityStore } = useStores();
  const editing = activityStore.entryToEdit;
  const todayEntry = (editing && editing.activityId === activity?._id) ? editing.entry : todayEntryOf(activity);
  const prefilledFor = useRef<string | null>(null);
  useEffect(() => {
    if (prefilledFor.current === question._id || value || !todayEntry || !questionText) {
      return;
    }
    const previous = answerFromEntry(todayEntry, questionText);
    if (previous) {
      prefilledFor.current = question._id;
      onChangeText(previous);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question._id, value, todayEntry, questionText]);

  const onChangeText = (newValue: string) => {
    treeNav.choices.updatePending({
      questionId: question._id,
      questionKey: question.key,
      // stamp the current context: a stale pending from an unfinished
      // session would otherwise leak its ids into this response
      surveyId: treeNav.content.survey?._id,
      pathId: treeNav.content.path?._id,
      sessionId: treeNav.content.session?._id,
      freeText: newValue,
    });
  };

  const [infoTitle, infoBody] = (question.description || '').split('|');

  const data: JournalEntryData = {
    value,
    onChangeText,
    infoTitle: infoTitle?.trim() || undefined,
    infoBody: infoBody?.trim() || undefined,
  };

  return children({
    questionId: question._id,
    questionKey: question.key,
    questionType: question.type,
    questionText,
    questionDescription: description,
    loading,
    data,
  });
};

export const ContainJournalEntry = observer(_ContainJournalEntry);
